import React from "react";
import { AlertTriangle, RotateCcw, RefreshCw } from "lucide-react";
import logger from "../utils/logger";

/**
 * ErrorBoundary - branded React error boundary.
 *
 * Catches render-time exceptions in its subtree, reports the caught error to
 * the structured logger, and renders a recoverable fallback instead of letting
 * the whole React tree unmount to a blank white screen.
 *
 * Two visual modes via the `variant` prop:
 *   "page"    -> full-viewport branded fallback with Try again + Reload actions.
 *               Use at the app root and around full-page routes.
 *   "section" -> compact inline panel. Use to wrap a widget/table so one bad
 *               row degrades to a contained error card rather than blanking the
 *               surrounding page.
 *
 * Recovery model:
 *   - "Try again" resets boundary state so the subtree re-mounts and re-renders
 *     (good for transient/shape-drift errors once upstream data recovers).
 *   - "Reload" does a hard window reload (page variant only).
 *   - An optional `resetKeys` array auto-resets the boundary when any key
 *     changes (e.g. route path, filter), so a recovered navigation clears a
 *     stale error without a manual click.
 *
 * Motion is intentionally minimal and reduced-motion friendly: a single CSS
 * fade/translate on mount that is disabled under prefers-reduced-motion via the
 * `motion-reduce:` utilities. No JS-driven animation.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const label = this.props.label || this.props.variant || "ErrorBoundary";
    logger.error(`[ErrorBoundary:${label}] Caught render error`, {
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
    });
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, errorInfo);
      } catch {
        // never let a reporting callback re-throw inside the boundary
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Auto-reset when any resetKey changes (e.g. route or filter changed),
    // so a recovered navigation clears a stale error without a manual click.
    if (!this.state.hasError) return;
    const prev = prevProps.resetKeys || [];
    const next = this.props.resetKeys || [];
    if (prev.length !== next.length || next.some((k, i) => k !== prev[i])) {
      this.handleReset();
    }
  }

  handleReset() {
    if (typeof this.props.onReset === "function") {
      try {
        this.props.onReset();
      } catch {
        // ignore
      }
    }
    this.setState({ hasError: false, error: null });
  }

  handleReload() {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback({
        error: this.state.error,
        reset: this.handleReset,
        reload: this.handleReload,
      });
    }

    if (this.props.variant === "section") {
      return (
        <SectionFallback
          title={this.props.title}
          description={this.props.description}
          onReset={this.handleReset}
        />
      );
    }

    return (
      <PageFallback
        title={this.props.title}
        description={this.props.description}
        onReset={this.handleReset}
        onReload={this.handleReload}
      />
    );
  }
}

const PageFallback = ({ title, description, onReset, onReload }) => (
  <div className="min-h-[100dvh] bg-background text-white flex items-center justify-center px-4 py-24">
    <div className="w-full max-w-md motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-300">
      {/* Double-bezel: outer shell + inner core */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-1.5 shadow-xl">
        <div className="rounded-xl border border-white/10 bg-card p-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/20">
            <AlertTriangle className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            {title || "Something went wrong"}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {description ||
              "This page hit an unexpected error. Your account and balance are safe. Try again, or reload if it keeps happening."}
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 font-semibold text-primary-foreground transition-colors duration-200 hover:bg-primary/90 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-card cursor-pointer"
            >
              <RotateCcw className="h-4 w-4" />
              Try again
            </button>
            <button
              type="button"
              onClick={onReload}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 font-medium text-white transition-colors duration-200 hover:bg-secondary active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-border focus:ring-offset-2 focus:ring-offset-card cursor-pointer"
            >
              <RefreshCw className="h-4 w-4" />
              Reload page
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SectionFallback = ({ title, description, onReset }) => (
  <div className="rounded-xl border border-red-700/60 bg-red-900/20 p-6 text-center motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200">
    <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-red-400" />
    <h2 className="text-base font-semibold text-white">
      {title || "This section could not be displayed"}
    </h2>
    <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
      {description ||
        "An unexpected error stopped this section from rendering. The rest of the page is unaffected."}
    </p>
    <button
      type="button"
      onClick={onReset}
      className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-secondary active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
    >
      <RotateCcw className="h-4 w-4" />
      Try again
    </button>
  </div>
);

export default ErrorBoundary;
